import { createContext, createElement, use, useContext, useRef, useEffect, Fragment, useCallback } from 'react';
import { isValidElementType } from 'react-is';

var GlitzContext = createContext(void 0);
var StreamContext = createContext(false);
var ThemeContext = createContext(void 0);
var ComposeContext = createContext(void 0);
var emptyComposeContext = { value: void 0 };

function GlitzProvider(props) {
    return createElement(GlitzContext.Provider, { value: props.glitz }, createElement(StreamContext.Provider, { value: !!props.stream }, props.children));
}

function ThemeProvider(props) {
    return createElement(ThemeContext.Provider, { value: props.theme }, props.children);
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var SECRET_GLITZ_PROPERTY = '__GLITZ__';
var DECORATOR_TYPE = 1;
var ELEMENT_TYPE = 2;
var ELEMENT_LIKE_TYPE = 3;
var FORWARD_STYLE_TYPE = 4;

function applyClassName(component) {
    var _a;
    return _a = {},
        _a[SECRET_GLITZ_PROPERTY] = ELEMENT_LIKE_TYPE,
        _a.value = component,
        _a;
}
function isElementLikeType(type) {
    return type[SECRET_GLITZ_PROPERTY] === ELEMENT_LIKE_TYPE;
}

function createElementType(tag) {
    var _a;
    return _a = {},
        _a[SECRET_GLITZ_PROPERTY] = ELEMENT_TYPE,
        _a.value = tag,
        _a;
}
function isElementType(type) {
    return type[SECRET_GLITZ_PROPERTY] === ELEMENT_TYPE;
}
function createPredefined(tag) {
    return function () {
        var styles = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            styles[_i] = arguments[_i];
        }
        return factory(createElementType(tag), styles);
    };
}
function assignPredefined(target) {
    for (var _i = 0, predefinedElements_1 = predefinedElements; _i < predefinedElements_1.length; _i++) {
        var tag = predefinedElements_1[_i];
        target[tag[0].toUpperCase() + tag.slice(1)] = factory(createElementType(tag), []);
        target[tag] = createPredefined(tag);
    }
    return target;
}
var predefinedElements = [
    'a',
    'abbr',
    'address',
    'area',
    'article',
    'aside',
    'audio',
    'b',
    'base',
    'bdi',
    'bdo',
    'big',
    'blockquote',
    'body',
    'br',
    'button',
    'canvas',
    'caption',
    'cite',
    'code',
    'col',
    'colgroup',
    'data',
    'datalist',
    'dd',
    'del',
    'details',
    'dfn',
    'dialog',
    'div',
    'dl',
    'dt',
    'em',
    'embed',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'head',
    'header',
    'hgroup',
    'hr',
    'html',
    'i',
    'iframe',
    'img',
    'input',
    'ins',
    'kbd',
    'keygen',
    'label',
    'legend',
    'li',
    'link',
    'main',
    'map',
    'mark',
    'menu',
    'menuitem',
    'meta',
    'meter',
    'nav',
    'noindex',
    'noscript',
    'object',
    'ol',
    'optgroup',
    'option',
    'output',
    'p',
    'param',
    'picture',
    'pre',
    'progress',
    'q',
    'rp',
    'rt',
    'ruby',
    's',
    'samp',
    'slot',
    'script',
    'section',
    'select',
    'small',
    'source',
    'span',
    'strong',
    'style',
    'sub',
    'summary',
    'sup',
    'table',
    'template',
    'tbody',
    'td',
    'textarea',
    'tfoot',
    'th',
    'thead',
    'time',
    'title',
    'tr',
    'track',
    'u',
    'ul',
    'var',
    'video',
    'wbr',
    'webview',
    'svg',
    'animate',
    'animateMotion',
    'animateTransform',
    'circle',
    'clipPath',
    'defs',
    'desc',
    'ellipse',
    'feBlend',
    'feColorMatrix',
    'feComponentTransfer',
    'feComposite',
    'feConvolveMatrix',
    'feDiffuseLighting',
    'feDisplacementMap',
    'feDistantLight',
    'feDropShadow',
    'feFlood',
    'feFuncA',
    'feFuncB',
    'feFuncG',
    'feFuncR',
    'feGaussianBlur',
    'feImage',
    'feMerge',
    'feMergeNode',
    'feMorphology',
    'feOffset',
    'fePointLight',
    'feSpecularLighting',
    'feSpotLight',
    'feTile',
    'feTurbulence',
    'filter',
    'foreignObject',
    'g',
    'image',
    'line',
    'linearGradient',
    'marker',
    'mask',
    'metadata',
    'mpath',
    'path',
    'pattern',
    'polygon',
    'polyline',
    'radialGradient',
    'rect',
    'stop',
    'switch',
    'symbol',
    'text',
    'textPath',
    'tspan',
    'use',
    'view',
];

function useTheme() {
    return use(ThemeContext);
}

function useGlitz(dirtyStyle) {
    var glitz = useContext(GlitzContext);
    if (!glitz) {
        throw new Error("The `<GlitzProvider>` doesn't seem to be used correctly because the core instance wasn't provided");
    }
    var theme = useTheme();
    var previousRef = useRef([
        glitz,
        theme,
    ]);
    var isCached = previousRef.current[0] === glitz && previousRef.current[1] === theme;
    var isValid = isCached && previousRef.current[2] === dirtyStyle;
    previousRef.current[0] = glitz;
    previousRef.current[1] = theme;
    previousRef.current[2] = dirtyStyle;
    var finalStyles;
    if (!isValid) {
        finalStyles = sanitizeStyle(dirtyStyle);
        if (previousRef.current[3]) {
            isValid = isCached && shallowEquals(previousRef.current[3], finalStyles);
        }
        previousRef.current[3] = finalStyles;
    }
    if (process.env.NODE_ENV !== 'production') {
        var hasWarnedCacheInvalidationsRef_1 = useRef(false);
        var totalCacheInvalidationsRef_1 = useRef(0);
        useEffect(function () {
            if (!isValid && typeof requestAnimationFrame === 'function' && !hasWarnedCacheInvalidationsRef_1.current) {
                totalCacheInvalidationsRef_1.current++;
                var currentCacheInvalidations_1 = totalCacheInvalidationsRef_1.current;
                requestAnimationFrame(function () {
                    requestAnimationFrame(function () {
                        if (totalCacheInvalidationsRef_1.current === currentCacheInvalidations_1) {
                            if (currentCacheInvalidations_1 > 5) {
                                console.warn("Multiple re-renders of a styled component with invalidated cache was detected. Either make sure it doesn't re-render or make sure the cache is intact. More info: https://git.io/fxYyd");
                                hasWarnedCacheInvalidationsRef_1.current = true;
                            }
                            totalCacheInvalidationsRef_1.current = 0;
                        }
                    });
                });
            }
        });
    }
    if (isValid) {
        return previousRef.current[4];
    }
    if (finalStyles.length === 0) {
        return void 0;
    }
    return (previousRef.current[4] = glitz.injectStyle(finalStyles, theme)) || void 0;
}
function sanitizeStyle(dirtyStyle) {
    if (typeof dirtyStyle === 'function') {
        return dirtyStyle();
    }
    if (Array.isArray(dirtyStyle)) {
        return flattenStyle(dirtyStyle);
    }
    if (dirtyStyle) {
        return [dirtyStyle];
    }
    return [];
}
function flattenStyle(dirtyStyles) {
    var styles = [];
    for (var _i = 0, dirtyStyles_1 = dirtyStyles; _i < dirtyStyles_1.length; _i++) {
        var style = dirtyStyles_1[_i];
        styles.push.apply(styles, sanitizeStyle(style));
    }
    return styles;
}
function shallowEquals(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    for (var i in a) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

function forwardStyle(component) {
    var _a;
    return _a = {},
        _a[SECRET_GLITZ_PROPERTY] = FORWARD_STYLE_TYPE,
        _a.value = component,
        _a;
}
function isForwardStyleType(type) {
    return type[SECRET_GLITZ_PROPERTY] === FORWARD_STYLE_TYPE;
}

function createComponent(type, statics) {
    return isStyledComponent(type) ? type[SECRET_GLITZ_PROPERTY](statics) : factory(type, statics);
}
function factory(type, statics) {
    var _a;
    var Component = isElementType(type) || isElementLikeType(type)
        ? function (_a) {
            var _b;
            var dynamic = _a.css, restProps = __rest(_a, ["css"]);
            var composed = useContext(ComposeContext);
            var className = combineClassNames(restProps.className, useGlitz([statics, dynamic, composed]));
            var node = createElement(type.value, __assign(__assign({}, restProps), { className: className }));
            if (composed && restProps.children) {
                node = createElement(ComposeContext.Provider, emptyComposeContext, node);
            }
            var stream = useContext(StreamContext);
            var glitz = useContext(GlitzContext);
            if (stream && glitz && 'getStyle' in glitz) {
                var style = glitz.getStyle(false, true);
                if (style) {
                    node = createElement(Fragment, null, createElement('style', (_b = {},
                        _b["data-".concat(glitz.identifier)] = '',
                        _b.dangerouslySetInnerHTML = { __html: style },
                        _b)), node);
                }
            }
            return node;
        }
        : isForwardStyleType(type)
            ? function (_a) {
                var dynamic = _a.css, restProps = __rest(_a, ["css"]);
                var composed = useContext(ComposeContext);
                var compose = useCallback(function (additional) { return sanitizeStyle([additional, statics, dynamic, composed]); }, [composed, dynamic]);
                var node = createElement(type.value, __assign(__assign({}, restProps), { compose: compose }));
                if (composed && restProps.children) {
                    node = createElement(ComposeContext.Provider, emptyComposeContext, node);
                }
                return node;
            }
            : function (_a) {
                var dynamic = _a.css, restProps = __rest(_a, ["css"]);
                var composed = useContext(ComposeContext);
                var style = sanitizeStyle([statics, dynamic, composed]);
                var node = createElement(type, __assign({}, restProps));
                if (style.length > 0) {
                    node = createElement(ComposeContext.Provider, { value: style }, node);
                }
                return node;
            };
    var Styled = Object.assign(Component, (_a = {},
        _a[SECRET_GLITZ_PROPERTY] = function (additional) {
            var NewStyled = factory(type, [statics, additional]);
            if (process.env.NODE_ENV !== 'production') {
                NewStyled.displayName = Component.displayName;
            }
            return NewStyled;
        },
        _a));
    if (process.env.NODE_ENV !== 'production') {
        if (!isForwardStyleType(type)) {
            var inner = isElementType(type) || isElementLikeType(type) ? type.value : type;
            Styled.displayName = "Styled(".concat(typeof inner === 'string' ? inner : inner.displayName || inner.name || 'Unknown', ")");
        }
    }
    return Styled;
}
function combineClassNames(a, b) {
    return a && b ? "".concat(a, " ").concat(b) : a ? a : b;
}
function isStyledComponent(type) {
    return typeof type[SECRET_GLITZ_PROPERTY] === 'function';
}

function createDecorator(dirtyStyle) {
    var _a;
    var style = sanitizeStyle(dirtyStyle);
    function decorator(arg1) {
        var arg2 = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            arg2[_i - 1] = arguments[_i];
        }
        if (arguments.length === 0) {
            return style;
        }
        if (isStyle(arg1)) {
            return createDecorator([style, arg1, arg2]);
        }
        return createComponent(arg1, [style, arg2]);
    }
    return Object.assign(decorator, (_a = {}, _a[SECRET_GLITZ_PROPERTY] = DECORATOR_TYPE, _a));
}
function isStyle(arg) {
    return (arg === false ||
        typeof arg === 'undefined' ||
        (typeof arg === 'function' && arg[SECRET_GLITZ_PROPERTY] === DECORATOR_TYPE) ||
        (typeof arg === 'object' &&
            !isElementType(arg) &&
            !isElementLikeType(arg) &&
            !isForwardStyleType(arg) &&
            !isValidElementType(arg)));
}

function creator(arg1) {
    var arg2 = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        arg2[_i - 1] = arguments[_i];
    }
    return isStyle(arg1) ? createDecorator([arg1, arg2]) : createComponent(arg1, arg2);
}
var createStyled = creator;

var styled = assignPredefined(createStyled);

export { GlitzProvider, ThemeProvider, applyClassName, forwardStyle, styled, useGlitz as useStyle, useTheme };
